import { useEffect, useRef, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/drop-001", label: "Drop 001" },
  { to: "/contraband", label: "Contraband" },
  { to: "/about", label: "About" },
  { to: "/sizing", label: "Sizing" },
  { to: "/shipping-returns", label: "Shipping" },
  { to: "/contact", label: "Contact" },
];

const MENU_ID = "fbx-mobile-menu";

export default function Header() {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Bind/unbind menu-close listeners while open
  useEffect(() => {
    if (!open) return;

    function close() {
      setOpen(false);
    }
    function onPointerDown(e) {
      // Ignore clicks inside the menu or on the toggle button itself
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      close();
    }
    function onKeyDown(e) {
      if (e.key === "Escape") {
        close();
        toggleRef.current?.focus();
      }
    }
    // Capture phase so we close before any in-page click handlers fire
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close);
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-[#2a1a3a] bg-[#050010]/85 backdrop-blur-md"
    >
      <div className="border-b border-[#1a0a2a]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-1.5 flex items-center justify-between text-[10px] font-mono tracking-[0.2em] uppercase text-[#b4a3c7]">
          <span data-testid="signal-status" className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#00ff41] rounded-full shadow-[0_0_8px_#00ff41] animate-pulse" />
            Signal // Live
          </span>
          <span className="hidden md:inline">Post Hominem · Drop 001 Preorder Open · USD</span>
          <span>SS26 // TX_001</span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="inline-flex items-center gap-2.5 glitch">
          <img
            src="/brand/emblem.png"
            alt="FEMBOT NEX emblem"
            width={28}
            height={28}
            className="shrink-0 drop-shadow-[0_0_10px_rgba(255,20,147,0.65)]"
            draggable={false}
          />
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg md:text-xl tracking-tight">
              FEMBOT<span className="text-[#ff1493]">·</span>NEX
            </span>
            <span className="mt-1 font-mono text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#c084fc]">
              POST HOMINEM
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin/login"
            data-testid="nav-admin"
            className="hidden md:inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.18em] uppercase text-[#7a6a90] hover:text-white transition-colors"
          >
            <Lock size={12} strokeWidth={1.5} /> Admin
          </Link>
          <button
            ref={toggleRef}
            data-testid="mobile-nav-toggle"
            type="button"
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((s) => !s)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls={MENU_ID}
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          ref={menuRef}
          id={MENU_ID}
          data-testid="mobile-menu"
          role="menu"
          aria-label="Primary"
          className="lg:hidden border-t border-[#2a1a3a] bg-[#05000f]"
        >
          <div className="max-w-[1400px] mx-auto px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                role="menuitem"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-3 font-mono text-xs tracking-[0.2em] uppercase border-b border-[#1a0a2a] ${
                    isActive ? "text-[#ff1493]" : "text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              role="menuitem"
              data-testid="mobile-nav-admin"
              className="block px-3 py-3 font-mono text-xs tracking-[0.2em] uppercase text-[#7a6a90]"
            >
              Admin Access →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
